import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../api';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Session configuration
  const SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  const REFRESH_THRESHOLD = 15 * 60 * 1000; // 15 minutes before expiry

  // Initialize session from localStorage
  useEffect(() => {
    const initializeSession = () => {
      try {
        const storedAuth = localStorage.getItem('isLoggedIn') === 'true';
        const storedUserType = localStorage.getItem('userType');
        const storedEmail = localStorage.getItem('userEmail');
        const storedExpiry = localStorage.getItem('sessionExpiry');
        const storedLastActivity = localStorage.getItem('lastActivity');

        if (storedAuth && storedUserType && storedEmail) {
          const expiryTime = storedExpiry ? parseInt(storedExpiry) : null;
          const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity) : Date.now();

          // Check if session is still valid
          if (expiryTime && Date.now() < expiryTime) {
            setIsAuthenticated(true);
            setUser({
              email: storedEmail,
              type: storedUserType
            });
            setSessionExpiry(expiryTime);
            setLastActivity(lastActivityTime);
          } else {
            // Session expired, clear storage
            clearSession();
          }
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, []);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Check for inactivity timeout
  useEffect(() => {
    const checkInactivity = () => {
      if (isAuthenticated && lastActivity) {
        const timeSinceLastActivity = Date.now() - lastActivity;
        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          handleLogout('Session expired due to inactivity');
        }
      }
    };

    const interval = setInterval(checkInactivity, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [isAuthenticated, lastActivity]);

  // Check for session expiry
  useEffect(() => {
    const checkSessionExpiry = () => {
      if (isAuthenticated && sessionExpiry) {
        const timeUntilExpiry = sessionExpiry - Date.now();
        
        if (timeUntilExpiry <= 0) {
          handleLogout('Session has expired');
        } else if (timeUntilExpiry <= REFRESH_THRESHOLD) {
          // Show warning about session expiry
          showSessionWarning(timeUntilExpiry);
        }
      }
    };

    const interval = setInterval(checkSessionExpiry, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, sessionExpiry]);

  const showSessionWarning = (timeUntilExpiry) => {
    const minutes = Math.ceil(timeUntilExpiry / (60 * 1000));
    
    // Show browser notification or modal
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Session Expiring Soon', {
        body: `Your session will expire in ${minutes} minutes. Click to extend.`,
        icon: '/favicon.ico'
      });
    }

    // Show in-app notification
    const shouldExtend = window.confirm(
      `Your session will expire in ${minutes} minutes. Would you like to extend it?`
    );
    
    if (shouldExtend) {
      extendSession();
    }
  };

  const extendSession = useCallback(() => {
    if (isAuthenticated) {
      const newExpiry = Date.now() + SESSION_DURATION;
      setSessionExpiry(newExpiry);
      setLastActivity(Date.now());
      
      localStorage.setItem('sessionExpiry', newExpiry.toString());
      localStorage.setItem('lastActivity', Date.now().toString());
      
      console.log('Session extended');
    }
  }, [isAuthenticated]);

  const login = useCallback((userData) => {
    const expiryTime = Date.now() + SESSION_DURATION;
    
    setIsAuthenticated(true);
    setUser(userData);
    setSessionExpiry(expiryTime);
    setLastActivity(Date.now());
    
    // Store in localStorage
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userType', userData.type);
    localStorage.setItem('sessionExpiry', expiryTime.toString());
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const logout = useCallback(async () => {
    // Attempt to notify backend and clear httpOnly cookies
    try {
      const userType = user?.type || localStorage.getItem('userType');
      const endpoint = userType === 'admin'
        ? `${API_BASE_URL}/api/admin/logout`
        : `${API_BASE_URL}/api/vendors/logout`;
      
      // For admin logout, also send the JWT token in Authorization header
      const headers = {};
      if (userType === 'admin') {
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
          headers['Authorization'] = `Bearer ${adminToken}`;
        }
      }
      
      await fetch(endpoint, { 
        method: 'POST', 
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }).catch(() => {});
    } catch (_) {}
    handleLogout('User logged out');
  }, [user]);

  const handleLogout = useCallback((reason) => {
    console.log('Logging out:', reason);
    
    // Store user type before clearing
    const userType = user?.type;
    // Determine if current session is admin-context before clearing storage
    const isAdminContext = (
      userType === 'admin' ||
      !!localStorage.getItem('adminToken') ||
      !!localStorage.getItem('adminApiKey') ||
      !!localStorage.getItem('ADMIN_API_KEY') ||
      (typeof window !== 'undefined' && (window.location.hash || '').includes('admin'))
    );
    
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpiry(null);
    setLastActivity(null);
    
    // Clear localStorage
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userType');
    localStorage.removeItem('sessionExpiry');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('adminApiKey');
    localStorage.removeItem('ADMIN_API_KEY');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('token');
    localStorage.removeItem('vendorData');

    // Best-effort clear accessible cookies (non-httpOnly)
    try {
      document.cookie.split(';').forEach(c => {
        const name = c.split('=')[0]?.trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    } catch (_) {}
    
    // Redirect to appropriate login page; force admin login for admin context
    if (isAdminContext) {
      window.location.href = `${window.location.origin}/#/admin-login`;
    } else if (userType === 'vendor') {
      window.location.href = `${window.location.origin}/#/vendor-login`;
    } else {
      window.location.href = `${window.location.origin}/#/vendor-login`;
    }
  }, [user]);

  const clearSession = useCallback(() => {
    // Store user type before clearing
    const userType = user?.type;
    // Determine if current session is admin-context before clearing storage
    const isAdminContext = (
      userType === 'admin' ||
      !!localStorage.getItem('adminToken') ||
      !!localStorage.getItem('adminApiKey') ||
      !!localStorage.getItem('ADMIN_API_KEY') ||
      (typeof window !== 'undefined' && (window.location.hash || '').includes('admin'))
    );
    
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpiry(null);
    setLastActivity(null);
    
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userType');
    localStorage.removeItem('sessionExpiry');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('adminApiKey');
    localStorage.removeItem('ADMIN_API_KEY');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('token');
    localStorage.removeItem('vendorData');
    
    // Redirect to appropriate login page; force admin login for admin context
    if (isAdminContext) {
      window.location.href = `${window.location.origin}/#/admin-login`;
    } else if (userType === 'vendor') {
      window.location.href = `${window.location.origin}/#/vendor-login`;
    } else {
      window.location.href = `${window.location.origin}/#/vendor-login`;
    }
  }, [user]);

  const validateSession = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      // For admin users, check if API key is still valid
      if (user?.type === 'admin') {
        // Prefer JWT token stored as adminToken
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          // Fallback to legacy adminApiKey if present
          const legacyKey = localStorage.getItem('adminApiKey') || localStorage.getItem('ADMIN_API_KEY');
          if (!legacyKey) {
            handleLogout('Admin token not found');
            return false;
          }
        }
        
        // Check if the session hasn't expired
        if (sessionExpiry && Date.now() >= sessionExpiry) {
          handleLogout('Session expired');
          return false;
        }

        // For admin users, we can also validate the JWT token
        if (adminToken) {
          try {
            // Decode JWT token to check expiry
            const payload = JSON.parse(atob(adminToken.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              handleLogout('JWT token expired');
              return false;
            }
          } catch (e) {
            console.warn('JWT token validation failed:', e);
            // Continue with session expiry check
          }
        }
      }

      // For vendor users, validate JWT token
      if (user?.type === 'vendor') {
        const token = localStorage.getItem('token');
        if (!token) {
          handleLogout('Vendor token not found');
          return false;
        }

        // Check if the session hasn't expired
        if (sessionExpiry && Date.now() >= sessionExpiry) {
          handleLogout('Session expired');
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      handleLogout('Session validation failed');
      return false;
    }
  }, [isAuthenticated, user, sessionExpiry, handleLogout]);

  const getTimeUntilExpiry = useCallback(() => {
    if (!sessionExpiry) return null;
    return Math.max(0, sessionExpiry - Date.now());
  }, [sessionExpiry]);

  const getTimeSinceLastActivity = useCallback(() => {
    if (!lastActivity) return null;
    return Date.now() - lastActivity;
  }, [lastActivity]);

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    extendSession,
    validateSession,
    getTimeUntilExpiry,
    getTimeSinceLastActivity,
    sessionExpiry,
    lastActivity
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
